// Color math for the skins: hex colors, mixing, and OKLab, a color space in which equal steps
// look equally different. Lightness in OKLab (0 = black, 1 = white) is how bright a color
// looks, so a recolored drawing keeps its light and dark parts.

const clamp01 = v => Math.max(0, Math.min(1, v));

export function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
}
export const rgbToHex = rgb => '#' + rgb.map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('').toUpperCase();

// f < 1 darkens toward black, f > 1 lightens toward white.
export function mixHex(hex, f) {
  return rgbToHex(hexToRgb(hex).map(v => (f < 1 ? v * f : v + (255 - v) * (f - 1))));
}

// t of the way from color a to color b (0: a, 1: b).
export function blendHex(a, b, t) {
  const p = hexToRgb(a), q = hexToRgb(b);
  return rgbToHex(p.map((v, i) => v + (q[i] - v) * t));
}

const toLinear = c => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = c => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

export function toLab(hex) {
  const [r, g, b] = hexToRgb(hex).map(v => toLinear(v / 255));
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s];
}

export function fromLab([L, A, B]) {
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.2914855480 * B) ** 3;
  const rgb = [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s];
  return rgbToHex(rgb.map(c => toGamma(clamp01(c)) * 255));
}

export const lightness = hex => toLab(hex)[0];

// A ramp: colors from dark to light. at(L) is the ramp's color with lightness L, blended in
// OKLab between the two stops around it. Asking for a stop's own lightness returns that stop exactly.
export function ramp(stops) {
  const pts = stops.map(hex => ({ hex, lab: toLab(hex) })).sort((p, q) => p.lab[0] - q.lab[0]);
  return {
    at(L) {
      if (L <= pts[0].lab[0]) return pts[0].hex;
      for (let i = 1; i < pts.length; i++) {
        const p = pts[i - 1], q = pts[i];
        if (L > q.lab[0]) continue;
        if (L === q.lab[0]) return q.hex;
        const t = (L - p.lab[0]) / (q.lab[0] - p.lab[0]);
        return fromLab(p.lab.map((v, k) => v + (q.lab[k] - v) * t));
      }
      return pts[pts.length - 1].hex;
    },
  };
}
